import React, { useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe } from 'lucide-react';
import { Language } from '../types/language';

const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage, userCountry } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'zh', name: 'Chinese', nativeName: '中文' },
    { code: 'ko', name: 'Korean', nativeName: '한국어' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    setIsOpen(false);
    const announcement = document.getElementById('language-change-announcement');
    if (announcement) {
      announcement.textContent = `Language changed to ${languages.find(lang => lang.code === newLanguage)?.name}`;
    }
  };

  return (
    <div className="relative">
      <button 
        ref={buttonRef}
        className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded-lg"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Select language (current: ${languages.find(lang => lang.code === language)?.name})`}
      >
        <Globe className="h-5 w-5" aria-hidden="true" />
        <span className="hidden md:inline text-sm font-medium">
          {languages.find(lang => lang.code === language)?.nativeName}
          {userCountry && <span className="ml-1 text-xs">({userCountry})</span>}
        </span>
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-48 blur-bg border border-black/5 dark:border-white/5 rounded-lg shadow-xl z-50"
          role="listbox"
          aria-label="Select language"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code as Language)}
              className={`block w-full text-left px-4 py-3 text-sm transition-colors ${
                language === lang.code 
                  ? 'text-primary dark:text-primary-light bg-light-card dark:bg-dark-card' 
                  : 'text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-light hover:bg-light-card dark:hover:bg-dark-card'
              }`}
              role="option"
              aria-selected={language === lang.code}
              lang={lang.code}
            >
              <span className="flex items-center">
                <span className="flex-grow">{lang.nativeName}</span>
                {language === lang.code && (
                  <span className="text-primary dark:text-primary-light">✓</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
      
      <div 
        id="language-change-announcement" 
        className="sr-only" 
        role="status" 
        aria-live="polite"
      ></div>
    </div>
  );
};

export default LanguageSwitcher;